import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  getMyProfile, updateMyProfile, uploadPhoto, uploadPromoPhoto,
  submitProfile, getGenres, getInstruments, changePassword,
} from '../services/api';

const PERFORMANCE_TYPES = [
  { value: 'solo',        label: 'Solo'        },
  { value: 'duo',         label: 'Duo'         },
  { value: 'band',        label: 'Band'        },
  { value: 'dj',          label: 'DJ'          },
  { value: 'spoken_word', label: 'Spoken Word' },
  { value: 'other',       label: 'Other'       },
];

const AMP_OPTIONS = ['DI', 'Mic', 'DI and Mic'];

function ProfileEditPage() {
  const navigate      = useNavigate();
  const user          = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const promoPhotoRef   = useRef();
  const profilePhotoRef = useRef();
  const bioRef          = useRef();
  const promoRef        = useRef();
  const debounceSaveRef = useRef(null);
  const saveDraftRef    = useRef(null);

  const [loading, setLoading]                   = useState(true);
  const [saving, setSaving]                     = useState(false);
  const [autosaveStatus, setAutosaveStatus]     = useState(''); // '' | 'saving' | 'saved' | 'error'
  const [profileLoaded, setProfileLoaded]       = useState(false);
  const [saveMsg, setSaveMsg]                   = useState('');
  const [error, setError]                       = useState('');
  const [genres, setGenres]                     = useState([]);
  const [instruments, setInstruments]           = useState([]);
  const [promoUploading, setPromoUploading]       = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl]     = useState('');
  const [profileUploading, setProfileUploading]   = useState(false);
  const [pwForm, setPwForm]                       = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving]                   = useState(false);
  const [pwMsg, setPwMsg]                         = useState('');
  const [pwError, setPwError]                     = useState('');
  const [submitting, setSubmitting]               = useState(false);
  const [showSubmitModal, setShowSubmitModal]   = useState(false);
  const [bioError, setBioError]                 = useState('');
  const [promoPhotoError, setPromoPhotoError]   = useState('');

  // Submission tracking
  const [submittedAt, setSubmittedAt]       = useState(null);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);

  // Form state
  const [form, setForm] = useState({
    real_name: '', stage_name: '', bio: '', phone: '',
    performance_type: '', performers_on_stage: '', set_length_min: '', set_length_max: '',
    website: '', instagram: '', facebook: '', spotify: '', soundcloud: '', youtube: '', bandcamp: '',
    is_scala_member: false,
  });
  const [promoPhotoUrl,   setPromoPhotoUrl]   = useState('');
  const [selectedGenres, setSelectedGenres]   = useState([]);
  const [stage, setStage]   = useState({ vocal_mics: 0 });
  const [instrRows, setInstrRows] = useState([]);
  const [media, setMedia]   = useState([{ url: '', title: '', media_type: 'link' }]);

  useEffect(() => {
    Promise.all([getMyProfile(), getGenres(), getInstruments()])
      .then(([profRes, genresRes, instrRes]) => {
        const p = profRes.data;
        setForm({
          real_name:          p.real_name          || '',
          stage_name:         p.stage_name         || '',
          bio:                p.bio                || '',
          phone:              p.phone              || '',
          performance_type:    p.performance_type    || '',
          performers_on_stage: p.performers_on_stage ?? '',
          set_length_min:     p.set_length_min     ?? '',
          set_length_max:     p.set_length_max     ?? '',
          website:            p.website            || '',
          instagram:          p.instagram          || '',
          facebook:           p.facebook           || '',
          spotify:            p.spotify            || '',
          soundcloud:         p.soundcloud         || '',
          youtube:            p.youtube            || '',
          bandcamp:           p.bandcamp           || '',
          is_scala_member:    p.is_scala_member    || false,
        });
        setPromoPhotoUrl(p.promo_photo_url || '');
        setProfilePhotoUrl(p.profile_photo_url || '');
        setSubmittedAt(p.submitted_at || null);
        setHasUnsavedEdits(p.has_unsaved_edits || false);
        setSelectedGenres((p.genres || []).map(g => g.id));
        setGenres(genresRes.data);
        setInstruments(instrRes.data);

        if (p.stage) {
          setStage({ vocal_mics: p.stage.vocal_mics || 0 });
        }

        setInstrRows((p.instruments || []).map(i => ({
          instrument_type_id: i.instrument_type_id,
          specify_text:       i.specify_text  || '',
          amplification:      i.amplification || '',
          drum_type:          i.drum_type     || '',
        })));

        if (p.media && p.media.length > 0) {
          setMedia(p.media.map(m => ({ url: m.url || '', title: m.title || '', media_type: m.media_type || 'link' })));
        }

        setProfileLoaded(true);
      })
      .catch(err => {
        if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
        else setError('Failed to load profile.');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const toggleGenre = (id) => {
    setSelectedGenres(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
    scheduleAutosave();
  };

  const genreBubbleStyle = (selected) => ({
    padding: '6px 14px',
    borderRadius: '9999px',
    fontSize: '13px',
    border: selected ? '1px solid #0ea5e9' : '1px solid #e5e7eb',
    background: selected ? '#0ea5e9' : '#f3f4f6',
    color: selected ? '#ffffff' : '#374151',
    cursor: 'pointer',
    fontWeight: selected ? 600 : 400,
    transition: 'all 0.12s',
    display: 'inline-flex',
    alignItems: 'center',
  });

  const handlePromoPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    setPromoUploading(true);
    setError('');
    setPromoPhotoError('');
    try {
      const res = await uploadPromoPhoto(fd);
      setPromoPhotoUrl(res.data.promo_photo_url);
    } catch {
      setError('Promo photo upload failed.');
    } finally {
      setPromoUploading(false);
      e.target.value = '';
    }
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('photo', file);
    setProfileUploading(true);
    try {
      const res = await uploadPhoto(fd);
      setProfilePhotoUrl(res.data.profile_photo_url);
    } catch {
      setError('Profile photo upload failed.');
    } finally {
      setProfileUploading(false);
      e.target.value = '';
    }
  };

  const handleChangePassword = async () => {
    setPwMsg(''); setPwError('');
    if (!pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password) {
      setPwError('All password fields are required.'); return;
    }
    if (pwForm.new_password.length < 8) {
      setPwError('New password must be at least 8 characters.'); return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('New passwords do not match.'); return;
    }
    setPwSaving(true);
    try {
      await changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwMsg('Password changed successfully.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwError(err.response?.data?.error || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  };

  // ── Core save function (used by both autosave and manual Save button) ──────
  const saveDraft = async () => {
    if (!profileLoaded || submitting || saving) return false;
    setSaving(true);
    setAutosaveStatus('saving');
    try {
      const res = await updateMyProfile({
        ...form,
        genre_ids:    selectedGenres,
        stage,
        instruments:  instrRows,
        media:        media.filter(m => m.url),
      });
      if (submittedAt) setHasUnsavedEdits(res.data.has_unsaved_edits ?? true);
      setAutosaveStatus('saved');
      setTimeout(() => setAutosaveStatus(s => s === 'saved' ? '' : s), 3000);
      return true;
    } catch (err) {
      console.error('Save failed:', err);
      setAutosaveStatus('error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Keep ref pointing to latest saveDraft (fixes stale closure in debounced calls)
  saveDraftRef.current = saveDraft;

  const scheduleAutosave = () => {
    if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
    debounceSaveRef.current = setTimeout(() => saveDraftRef.current?.(), 1500);
  };

  const stageRequirementsMet = stage.vocal_mics !== 0 || instrRows.some(r => r.instrument_type_id);

  const handleSave = async () => {
    setSaveMsg(''); setError('');
    if (!form.real_name.trim() || !form.bio.trim() || !promoPhotoUrl || !stageRequirementsMet) {
      setError('Please complete all required fields: Real Name, Bio, Promo Photo and Stage Requirements.');
      return;
    }
    const ok = await saveDraft();
    if (ok) setSaveMsg('Profile saved.');
    else setError('Failed to save. Please try again.');
  };

  const handleSubmitClick = () => {
    // Validate mandatory fields
    let valid = true;
    setBioError('');
    setPromoPhotoError('');

    if (!form.bio || !form.bio.trim()) {
      setBioError('Bio is required before submitting your profile.');
      bioRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      valid = false;
    }
    if (!promoPhotoUrl) {
      setPromoPhotoError('A promo photo is required before submitting your profile.');
      if (valid) promoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      valid = false;
    }
    if (!valid) return;

    setShowSubmitModal(true);
  };

  const handleSubmitConfirm = async () => {
    setShowSubmitModal(false);
    setSubmitting(true); setError('');
    try {
      const res = await submitProfile();
      setSubmittedAt(res.data.submitted_at || new Date().toISOString());
      setHasUnsavedEdits(false);
      setSaveMsg('');
    } catch (err) {
      setError(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit button visibility logic
  const showSubmitButton = (() => {
    if (!submittedAt) return true;                   // never submitted
    if (!hasUnsavedEdits) return false;              // submitted, no edits
    return true;                                     // submitted + has edits
  })();

  const submitButtonDisabled = submitting;
  const submitButtonLabel = submitting ? 'Submitting…' : (!submittedAt ? 'Submit Profile' : 'Resubmit Profile');

  // Autosave status indicator
  const autosaveIndicator = autosaveStatus ? (
    <span style={{
      fontSize: '12px',
      color: autosaveStatus === 'saving' ? '#6b7280' : autosaveStatus === 'error' ? '#ef4444' : '#22c55e',
      transition: 'opacity 0.5s',
    }}>
      {autosaveStatus === 'saving' ? 'Saving...' : autosaveStatus === 'error' ? 'Save failed' : 'Saved ✓'}
    </span>
  ) : null;

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar user={user} />
      <div className="a-page" style={{ paddingTop: '2rem', textAlign: 'center', color: 'var(--muted)' }}>
        Loading your profile…
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar user={user} />

      <div className="a-page" style={{ maxWidth: 860 }}>

        {/* Header */}
        <div className="a-resp-stack" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--navy)' }}>My Performer Profile</h1>
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              This information is shared with SCALA organisers.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {autosaveIndicator}
            <button className="a-btn a-btn--ghost" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
            {showSubmitButton && (
              <button
                className="a-btn a-btn--navy"
                onClick={handleSubmitClick}
                disabled={submitButtonDisabled}
              >
                {submitButtonLabel}
              </button>
            )}
          </div>
        </div>

        {error   && <div className="a-error"   style={{ marginBottom: '1rem' }}>{error}</div>}
        {saveMsg && <div className="a-success" style={{ marginBottom: '1rem' }}>{saveMsg}</div>}

        {/* ── 1. About you ──────────────────────────────────────────────────── */}
        <div className="a-card" style={{ marginBottom: '1.25rem' }}>
          <h2 className="a-section-title">About you</h2>
          <div className="a-resp-stack" style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
            {/* Promo photo — left column */}
            <div style={{ flexShrink: 0 }} ref={promoRef}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Promo Photo <span className="a-required">*</span>
              </div>
              {promoUploading ? (
                <div style={{
                  width: 140, height: 90, borderRadius: 6, border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--muted)', fontSize: '0.8rem', background: '#f9fafb',
                }}>
                  Uploading…
                </div>
              ) : promoPhotoUrl ? (
                <img src={promoPhotoUrl} alt="Promo"
                  style={{ width: 140, height: 90, objectFit: 'cover', borderRadius: 6, display: 'block',
                    border: promoPhotoError ? '2px solid var(--red)' : '1px solid var(--border)' }} />
              ) : (
                <div style={{
                  width: 140, height: 90, borderRadius: 6,
                  border: promoPhotoError ? '2px dashed var(--red)' : '2px dashed var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--muted)', fontSize: '0.75rem', textAlign: 'center', lineHeight: 1.4,
                }}>
                  No promo<br />photo yet
                </div>
              )}
              <button type="button" onClick={() => { promoPhotoRef.current.click(); setPromoPhotoError(''); }} disabled={promoUploading}
                style={{ display: 'block', marginTop: 6, fontSize: '0.75rem', color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {promoPhotoUrl ? 'Change promo photo' : 'Upload promo photo'}
              </button>
              <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--muted)' }}>
                JPG, PNG or WebP · max 10 MB<br />Landscape recommended
              </p>
              {promoPhotoError && (
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--red)' }}>{promoPhotoError}</p>
              )}
              <input ref={promoPhotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePromoPhotoChange} />
            </div>
            {/* Name / contact fields — middle column */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div className="a-field" style={{ marginBottom: 0 }}>
                  <label className="a-label">Stage Name</label>
                  <input className="a-input" value={form.stage_name}
                    onChange={handleField('stage_name')}
                    onBlur={saveDraft}
                    placeholder="What you perform as" />
                </div>
                <div className="a-field" style={{ marginBottom: 0 }}>
                  <label className="a-label">Real Name <span className="a-required">*</span></label>
                  <input className="a-input" value={form.real_name}
                    onChange={handleField('real_name')}
                    onBlur={saveDraft} />
                </div>
                <div className="a-field" style={{ marginBottom: 0 }}>
                  <label className="a-label">Phone</label>
                  <input className="a-input" value={form.phone}
                    onChange={handleField('phone')}
                    onBlur={saveDraft}
                    type="tel" />
                </div>
              </div>
            </div>
            {/* Profile photo — right column */}
            <div style={{ flexShrink: 0, textAlign: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Profile Photo
              </div>
              {profileUploading ? (
                <div style={{ width: 80, height: 80, borderRadius: '50%', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: '0.75rem', background: '#f9fafb' }}>
                  …
                </div>
              ) : profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt="Profile"
                  style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)', display: 'block', margin: '0 auto' }} />
              ) : (
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: 'var(--muted)', margin: '0 auto' }}>
                  {form.real_name ? form.real_name[0].toUpperCase() : '?'}
                </div>
              )}
              <button type="button" onClick={() => profilePhotoRef.current.click()} disabled={profileUploading}
                style={{ display: 'block', marginTop: 6, fontSize: '0.75rem', color: 'var(--blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%', textAlign: 'center' }}>
                Change photo
              </button>
              <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: 'var(--muted)' }}>
                JPG, PNG or WebP · max 10 MB
              </p>
              <input ref={profilePhotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfilePhotoChange} />
            </div>
          </div>
          <div className="a-field" ref={bioRef}>
            <label className="a-label">
              Bio <span className="a-required">*</span>{' '}
              <span style={{ textTransform: 'none', fontWeight: 400 }}>(500 characters max)</span>
              {bioError && <span style={{ color: 'var(--red)', marginLeft: '0.5rem', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{bioError}</span>}
            </label>
            <textarea
              className="a-textarea"
              value={form.bio}
              onChange={e => { setForm(f => ({ ...f, bio: e.target.value.slice(0, 500) })); setBioError(''); }}
              onBlur={saveDraft}
              rows={4}
              placeholder="Tell SCALA organisers about you and your music…"
              style={bioError ? { borderColor: 'var(--red)' } : {}}
            />
            <div className="a-char-count">{form.bio.length} / 500</div>
          </div>
        </div>

        {/* ── 2. Stage requirements ─────────────────────────────────────────── */}
        <div className="a-card" style={{ marginBottom: '1.25rem' }}>
          <h2 className="a-section-title">Stage requirements <span className="a-required">*</span></h2>

          <div style={{ marginBottom: '1rem' }}>
            <div className="a-field" style={{ display: 'inline-block', marginBottom: 0 }}>
              <label className="a-label">Vocal mics</label>
              <select className="a-select" value={stage.vocal_mics} style={{ width: 90 }}
                onChange={e => setStage(s => ({ ...s, vocal_mics: parseInt(e.target.value) }))}
                onBlur={saveDraft}>
                {[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <label className="a-label" style={{ marginBottom: '0.5rem', display: 'block' }}>Instruments on stage</label>
          {instrRows.map((row, i) => {
            const instrType = instruments.find(t => t.id === row.instrument_type_id);
            const isDrumKit = instrType?.name === 'Drum Kit';
            return (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select className="a-select" value={row.instrument_type_id || ''} style={{ width: 180 }}
                  onChange={e => { setInstrRows(prev => prev.map((r, j) => j === i ? { ...r, instrument_type_id: parseInt(e.target.value), specify_text: '', amplification: '', drum_type: '' } : r)); scheduleAutosave(); }}>
                  <option value="">Select instrument…</option>
                  {instruments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {instrType?.allow_specify && (
                  <input className="a-input" placeholder="Specify…" style={{ width: 150 }}
                    value={row.specify_text}
                    onChange={e => setInstrRows(prev => prev.map((r, j) => j === i ? { ...r, specify_text: e.target.value } : r))}
                    onBlur={saveDraft} />
                )}
                {isDrumKit ? (
                  <select className="a-select" value={row.drum_type || ''} style={{ width: 140 }}
                    onChange={e => { setInstrRows(prev => prev.map((r, j) => j === i ? { ...r, drum_type: e.target.value } : r)); scheduleAutosave(); }}>
                    <option value="">Type…</option>
                    <option value="acoustic">Acoustic</option>
                    <option value="electronic">Electronic</option>
                  </select>
                ) : (
                  <select className="a-select" value={row.amplification || ''} style={{ width: 140 }}
                    onChange={e => { setInstrRows(prev => prev.map((r, j) => j === i ? { ...r, amplification: e.target.value } : r)); scheduleAutosave(); }}>
                    <option value="">Amplification…</option>
                    {AMP_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                )}
                <button type="button" className="a-btn a-btn--ghost a-btn--sm"
                  onClick={() => setInstrRows(prev => prev.filter((_, j) => j !== i))}>✕</button>
              </div>
            );
          })}
          <button type="button" className="a-btn a-btn--ghost a-btn--sm"
            onClick={() => setInstrRows(prev => [...prev, { instrument_type_id: '', specify_text: '', amplification: '', drum_type: '' }])}>
            + Add instrument
          </button>
        </div>

        {/* ── 3. Your act ───────────────────────────────────────────────────── */}
        <div className="a-card" style={{ marginBottom: '1.25rem' }}>
          <h2 className="a-section-title">Your act</h2>

          <div className="a-resp-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div className="a-field">
              <label className="a-label">Performance Type</label>
              <select className="a-select" value={form.performance_type}
                onChange={handleField('performance_type')}
                onBlur={saveDraft}>
                <option value="">Select…</option>
                {PERFORMANCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="a-field">
              <label className="a-label">Number of Performers on Stage</label>
              <input className="a-input" type="number" min="1" max="20"
                value={form.performers_on_stage}
                onChange={handleField('performers_on_stage')}
                onBlur={saveDraft}
                placeholder="e.g. 4" />
            </div>
          </div>

          <div className="a-field">
            <label className="a-label">Genres <span style={{ textTransform: 'none', fontWeight: 400 }}>(select all that apply)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {genres.map(g => (
                <button key={g.id} type="button" onClick={() => toggleGenre(g.id)}
                  style={genreBubbleStyle(selectedGenres.includes(g.id))}>
                  {g.name}
                </button>
              ))}
            </div>
          </div>

          <div className="a-resp-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <div className="a-field">
              <label className="a-label">Website</label>
              <input className="a-input" value={form.website}
                onChange={handleField('website')}
                onBlur={saveDraft}
                type="url" placeholder="https://…" />
            </div>
            <div className="a-field">
              <label className="a-label">Instagram</label>
              <input className="a-input" value={form.instagram}
                onChange={handleField('instagram')}
                onBlur={saveDraft}
                placeholder="@handle" />
            </div>
            <div className="a-field">
              <label className="a-label">Facebook</label>
              <input className="a-input" value={form.facebook}
                onChange={handleField('facebook')}
                onBlur={saveDraft}
                placeholder="Page URL or handle" />
            </div>
            <div className="a-field">
              <label className="a-label">Spotify</label>
              <input className="a-input" value={form.spotify}
                onChange={handleField('spotify')}
                onBlur={saveDraft}
                placeholder="Artist URL" />
            </div>
            <div className="a-field">
              <label className="a-label">SoundCloud</label>
              <input className="a-input" value={form.soundcloud}
                onChange={handleField('soundcloud')}
                onBlur={saveDraft}
                placeholder="soundcloud.com/…" />
            </div>
            <div className="a-field">
              <label className="a-label">YouTube</label>
              <input className="a-input" value={form.youtube}
                onChange={handleField('youtube')}
                onBlur={saveDraft}
                placeholder="Channel or video URL" />
            </div>
            <div className="a-field">
              <label className="a-label">Bandcamp</label>
              <input className="a-input" value={form.bandcamp}
                onChange={handleField('bandcamp')}
                onBlur={saveDraft}
                placeholder="https://yourname.bandcamp.com" />
            </div>
          </div>
        </div>

        {/* ── 4. Your music ─────────────────────────────────────────────────── */}
        <div className="a-card" style={{ marginBottom: '1.25rem' }}>
          <h2 className="a-section-title">Your music</h2>
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Add links to recordings, videos, or anywhere people can hear your music.
          </p>
          {media.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', alignItems: 'center' }}>
              <input className="a-input" placeholder="URL (Spotify, YouTube, SoundCloud…)"
                value={item.url} style={{ flex: '2' }}
                onChange={e => setMedia(prev => prev.map((m, j) => j === i ? { ...m, url: e.target.value } : m))}
                onBlur={saveDraft} />
              <input className="a-input" placeholder="Title / description"
                value={item.title} style={{ flex: '1' }}
                onChange={e => setMedia(prev => prev.map((m, j) => j === i ? { ...m, title: e.target.value } : m))}
                onBlur={saveDraft} />
              {media.length > 1 && (
                <button type="button" className="a-btn a-btn--ghost a-btn--sm"
                  onClick={() => setMedia(prev => prev.filter((_, j) => j !== i))}>✕</button>
              )}
            </div>
          ))}
          <button type="button" className="a-btn a-btn--ghost a-btn--sm"
            onClick={() => setMedia(prev => [...prev, { url: '', title: '', media_type: 'link' }])}>
            + Add another link
          </button>
        </div>

        {/* ── 5. SCALA membership ───────────────────────────────────────────── */}
        <div className="a-card" style={{ marginBottom: '1.5rem' }}>
          <h2 className="a-section-title">SCALA membership</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', fontSize: '0.9rem' }}>
            <button type="button"
              onClick={() => { setForm(f => ({ ...f, is_scala_member: !f.is_scala_member })); scheduleAutosave(); }}
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none',
                cursor: 'pointer',
                background: form.is_scala_member ? 'var(--blue)' : '#d1d5db', position: 'relative',
                transition: 'background 0.2s', flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute', top: 3,
                left: form.is_scala_member ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s',
              }} />
            </button>
            I am a current SCALA member
          </label>
        </div>

        {/* ── 6. Change password ────────────────────────────────────────────── */}
        <div className="a-card" style={{ marginBottom: '1.5rem' }}>
          <h2 className="a-section-title">Change password</h2>
          {pwMsg   && <div className="a-success" style={{ marginBottom: '1rem' }}>{pwMsg}</div>}
          {pwError && <div className="a-error"   style={{ marginBottom: '1rem' }}>{pwError}</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: 380 }}>
            <div className="a-field" style={{ marginBottom: 0 }}>
              <label className="a-label">Current password</label>
              <input className="a-input" type="password" value={pwForm.current_password}
                onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} />
            </div>
            <div className="a-field" style={{ marginBottom: 0 }}>
              <label className="a-label">New password <span style={{ textTransform: 'none', fontWeight: 400 }}>(min 8 characters)</span></label>
              <input className="a-input" type="password" value={pwForm.new_password}
                onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} />
            </div>
            <div className="a-field" style={{ marginBottom: 0 }}>
              <label className="a-label">Confirm new password</label>
              <input className="a-input" type="password" value={pwForm.confirm_password}
                onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))} />
            </div>
          </div>
          <button className="a-btn a-btn--ghost" style={{ marginTop: '1rem' }}
            onClick={handleChangePassword} disabled={pwSaving}>
            {pwSaving ? 'Saving…' : 'Change password'}
          </button>
        </div>

        {/* Bottom save/submit bar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          {saveMsg && <span style={{ color: '#15803d', fontSize: '0.875rem' }}>{saveMsg}</span>}
          {error   && <span style={{ color: 'var(--red)', fontSize: '0.875rem' }}>{error}</span>}
          {autosaveIndicator}
          <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '0.25rem' }}>
            <span className="a-required" style={{ fontSize: '14px' }}>*</span> Required field
          </span>
          <button className="a-btn a-btn--ghost" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
          {showSubmitButton && (
            <button
              className="a-btn a-btn--navy"
              onClick={handleSubmitClick}
              disabled={submitButtonDisabled}
            >
              {submitButtonLabel}
            </button>
          )}
        </div>
      </div>

      {/* Submit confirmation modal */}
      {showSubmitModal && (
        <div className="a-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowSubmitModal(false); }}>
          <div className="a-modal">
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              {!submittedAt ? 'Submit your profile?' : 'Resubmit your profile?'}
            </h2>
            <p style={{ color: 'var(--text)', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              {!submittedAt
                ? 'Once submitted, SCALA organisers will be notified and receive your profile.'
                : 'Resubmitting will notify SCALA organisers with your updated profile.'
              }
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setShowSubmitModal(false)}>Cancel</button>
              <button className="a-btn a-btn--navy" onClick={handleSubmitConfirm}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileEditPage;
